import { useEffect, useMemo, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { TableSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Inbox, Copy, Search, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/image-upload";
import { useStorageUrl } from "@/components/storage-image";
import { uploadFile, deleteByUrl, extractStoragePath } from "@/lib/storage";
import { slugify } from "@/lib/slug";

export const Route = createFileRoute("/_authenticated/store/products")({
  head: () => ({ meta: [{ title: "Produk Store — MI Al-Munajah Admin" }] }),
  component: ProductsPage,
});

type Status = "active" | "inactive" | "out_of_stock";
type Product = {
  id: string;
  name: string;
  slug: string;
  category_id: string | null;
  price: number;
  promo_price: number | null;
  short_description: string | null;
  description: string | null;
  specifications: Record<string, string> | null;
  sizes: string[] | null;
  colors: string[] | null;
  stock: number;
  status: Status;
  is_best_seller: boolean;
  is_new: boolean;
  is_promo: boolean;
  sort_order: number;
  whatsapp_number: string | null;
  order_instructions: string | null;
  purchase_info: string | null;
  main_image: string | null;
  created_at: string;
};
type Cat = { id: string; name: string; sort_order: number };
type Gallery = { id: string; image_url: string; sort_order: number };

const PAGE_SIZE = 20;

const emptyForm = {
  name: "", slug: "", category_id: "", price: 0, promo_price: 0,
  short_description: "", description: "", specifications: "",
  sizes: "", colors: "", stock: 0, status: "active" as Status,
  is_best_seller: false, is_new: false, is_promo: false, sort_order: 0,
  whatsapp_number: "", order_instructions: "", purchase_info: "", main_image: "",
};

function ProductsPage() {
  const [rows, setRows] = useCachedState<Product[]>("store.products:rows", []);
  const [cats, setCats] = useCachedState<Cat[]>("store.products:cats", []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [sort, setSort] = useState<string>("created_at-desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [gallery, setGallery] = useCachedState<Gallery[]>("store.products:gallery", []);
  const [delId, setDelId] = useState<string | null>(null);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: p }, { data: c }] = await Promise.all([
      supabase.from("store_products").select("*"),
      supabase.from("store_categories").select("id,name,sort_order").order("sort_order", { ascending: true }).order("name", { ascending: true }),
    ]);
    setRows((p ?? []) as Product[]);
    setCats((c ?? []) as Cat[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let r = rows.filter((x) => x.name.toLowerCase().includes(search.toLowerCase()));
    if (filterCat !== "all") r = r.filter((x) => x.category_id === filterCat);
    if (filterStatus !== "all") {
      if (filterStatus === "out_of_stock") {
        r = r.filter((x) => x.status === "out_of_stock" || (x.stock ?? 0) <= 0);
      } else {
        r = r.filter((x) => x.status === filterStatus);
      }
    }
    const [field, dir] = sort.split("-");
    r = [...r].sort((a, b) => {
      const av = (a as any)[field]; const bv = (b as any)[field];
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return dir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [rows, search, filterCat, filterStatus, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, filterCat, filterStatus]);

  const allSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    const s = new Set(selected);
    if (allSelected) pageRows.forEach((r) => s.delete(r.id));
    else pageRows.forEach((r) => s.add(r.id));
    setSelected(s);
  };
  const toggleOne = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setGallery([]); setOpen(true);
  };
  const openEdit = async (r: Product) => {
    setEditing(r);
    setForm({
      name: r.name, slug: r.slug, category_id: r.category_id ?? "",
      price: Number(r.price), promo_price: Number(r.promo_price ?? 0),
      short_description: r.short_description ?? "", description: r.description ?? "",
      specifications: r.specifications ? Object.entries(r.specifications).map(([k, v]) => `${k}: ${v}`).join("\n") : "",
      sizes: (r.sizes ?? []).join(", "), colors: (r.colors ?? []).join(", "),
      stock: r.stock, status: r.status,
      is_best_seller: r.is_best_seller, is_new: r.is_new, is_promo: r.is_promo,
      sort_order: r.sort_order, whatsapp_number: r.whatsapp_number ?? "",
      order_instructions: r.order_instructions ?? "", purchase_info: r.purchase_info ?? "",
      main_image: r.main_image ?? "",
    });
    const { data: g } = await supabase.from("store_product_images").select("*").eq("product_id", r.id).order("sort_order");
    setGallery((g ?? []) as Gallery[]);
    setOpen(true);
  };

  const parseSpecs = (raw: string): Record<string, string> => {
    const out: Record<string, string> = {};
    raw.split("\n").map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    return out;
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    setBusy(true);
    const nextStock = form.status === "out_of_stock" ? 0 : Number(form.stock) || 0;
    const payload = {
      name: form.name.trim(),
      slug: (form.slug || slugify(form.name)).trim(),
      category_id: form.category_id || null,
      price: Number(form.price) || 0,
      promo_price: form.promo_price ? Number(form.promo_price) : null,
      short_description: form.short_description.trim() || null,
      description: form.description.trim() || null,
      specifications: parseSpecs(form.specifications),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      stock: nextStock,
      status: form.status,
      is_best_seller: form.is_best_seller,
      is_new: form.is_new,
      is_promo: form.is_promo,
      sort_order: Number(form.sort_order) || 0,
      whatsapp_number: form.whatsapp_number.trim() || null,
      order_instructions: form.order_instructions.trim() || null,
      purchase_info: form.purchase_info.trim() || null,
      main_image: form.main_image || null,
    };
    const { error } = editing
      ? await supabase.from("store_products").update(payload).eq("id", editing.id)
      : await supabase.from("store_products").insert(payload);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Produk diperbarui" : "Produk dibuat");
    setOpen(false);
    load();
  };

  const remove = async () => {
    if (!delId) return;
    setBusy(true);
    const { error } = await supabase.from("store_products").delete().eq("id", delId);
    setBusy(false);
    setDelId(null);
    if (error) return toast.error(error.message);
    toast.success("Produk dihapus");
    load();
  };

  const duplicate = async (r: Product) => {
    const { id, created_at, ...rest } = r;
    void id; void created_at;
    const copy = { ...rest, name: r.name + " (Copy)", slug: slugify(r.name + " copy " + Date.now()) };
    const { error } = await supabase.from("store_products").insert(copy);
    if (error) return toast.error(error.message);
    toast.success("Produk diduplikasi");
    load();
  };

  const bulkUpdate = async (patch: Partial<Product>) => {
    if (selected.size === 0) return;
    const { error } = await supabase.from("store_products").update(patch as any).in("id", Array.from(selected));
    if (error) return toast.error(error.message);
    toast.success("Berhasil diperbarui");
    setSelected(new Set());
    load();
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    const { error } = await supabase.from("store_products").delete().in("id", Array.from(selected));
    setBusy(false);
    setBulkDelOpen(false);
    if (error) return toast.error(error.message);
    toast.success(`${selected.size} produk dihapus`);
    setSelected(new Set());
    load();
  };

  const uploadGalleryFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !editing) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const url = await uploadFile("store", file, `products/${editing.id}`);
        await supabase.from("store_product_images").insert({ product_id: editing.id, image_url: url, sort_order: gallery.length });
      }
      const { data: g } = await supabase.from("store_product_images").select("*").eq("product_id", editing.id).order("sort_order");
      setGallery((g ?? []) as Gallery[]);
      toast.success("Foto galeri ditambahkan");
    } catch (e) {
      toast.error("Upload gagal: " + (e as Error).message);
    } finally { setBusy(false); }
  };
  const removeGallery = async (g: Gallery) => {
    await deleteByUrl("store", extractStoragePath("store", g.image_url) ?? g.image_url);
    await supabase.from("store_product_images").delete().eq("id", g.id);
    setGallery((prev) => prev.filter((x) => x.id !== g.id));
  };

  return (
    <div>
      <PageHeader
        title="Produk"
        description="Katalog produk Toko Sekolah yang tampil di aplikasi Android."
        actions={<Button onClick={openCreate}><Plus className="w-4 h-4" /> Tambah Produk</Button>}
      />

      <Card className="p-4 rounded-2xl space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cari produk…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua kategori</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua status</SelectItem>
              <SelectItem value="active">Aktif</SelectItem>
              <SelectItem value="inactive">Nonaktif</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at-desc">Terbaru</SelectItem>
              <SelectItem value="created_at-asc">Terlama</SelectItem>
              <SelectItem value="name-asc">Nama A–Z</SelectItem>
              <SelectItem value="name-desc">Nama Z–A</SelectItem>
              <SelectItem value="price-asc">Harga termurah</SelectItem>
              <SelectItem value="price-desc">Harga termahal</SelectItem>
              <SelectItem value="sort_order-asc">Urutan</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-muted">
            <span className="text-sm font-medium">{selected.size} terpilih</span>
            <Button size="sm" variant="outline" onClick={() => bulkUpdate({ status: "active" })}>Publish</Button>
            <Button size="sm" variant="outline" onClick={() => bulkUpdate({ status: "inactive" })}>Nonaktifkan</Button>
            <Button size="sm" variant="destructive" onClick={() => setBulkDelOpen(true)}><Trash2 className="w-4 h-4" /> Hapus</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}><X className="w-4 h-4" /></Button>
          </div>
        )}

        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} /></TableHead>
                <TableHead>Produk</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead className="text-right">Harga</TableHead>
                <TableHead className="text-right">Stok</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Label</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={8} />
              ) : pageRows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-12">
                  <div className="flex flex-col items-center text-muted-foreground gap-2">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Inbox className="w-6 h-6" /></div>
                    <div className="font-medium text-foreground">Belum ada produk</div>
                  </div>
                </TableCell></TableRow>
              ) : pageRows.map((r) => (
                <TableRow key={r.id} data-state={selected.has(r.id) ? "selected" : undefined}>
                  <TableCell><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <ProductThumb path={r.main_image} />
                      <div>
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.slug}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{cats.find((c) => c.id === r.category_id)?.name ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="font-semibold">{formatIDR(Number(r.promo_price ?? r.price))}</div>
                    {r.promo_price && <div className="text-xs text-muted-foreground line-through">{formatIDR(Number(r.price))}</div>}
                  </TableCell>
                  <TableCell className="text-right">{r.stock}</TableCell>
                  <TableCell>
                    <StatusBadge s={r.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.is_best_seller && <Badge variant="outline" className="bg-amber-500/15 text-amber-700 border-amber-500/30">Best</Badge>}
                      {r.is_new && <Badge variant="outline" className="bg-blue-500/15 text-blue-700 border-blue-500/30">Baru</Badge>}
                      {r.is_promo && <Badge variant="outline" className="bg-pink-500/15 text-pink-700 border-pink-500/30">Promo</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => duplicate(r)} title="Duplikat"><Copy className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">{filtered.length} produk</div>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span>Hal. {page} / {totalPages}</span>
            <Button size="icon" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
            <DialogDescription>Lengkapi data produk untuk ditampilkan di katalog Android.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basic">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="basic">Info Dasar</TabsTrigger>
              <TabsTrigger value="price">Harga & Stok</TabsTrigger>
              <TabsTrigger value="detail">Detail</TabsTrigger>
              <TabsTrigger value="photo">Foto</TabsTrigger>
              <TabsTrigger value="order">Pemesanan</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div>
                <Label>Nama Produk <span className="text-destructive">*</span></Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })} />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="otomatis dari nama" />
              </div>
              <div>
                <Label>Kategori</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tanpa kategori</SelectItem>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as Status, stock: v === "out_of_stock" ? 0 : form.stock })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="inactive">Nonaktif</SelectItem>
                    
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <LabelSwitch label="Best Seller" checked={form.is_best_seller} onChange={(v) => setForm({ ...form, is_best_seller: v })} />
                <LabelSwitch label="Produk Baru" checked={form.is_new} onChange={(v) => setForm({ ...form, is_new: v })} />
                <LabelSwitch label="Promo" checked={form.is_promo} onChange={(v) => setForm({ ...form, is_promo: v })} />
              </div>
              <div>
                <Label>Urutan</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              </div>
            </TabsContent>

            <TabsContent value="price" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Harga (Rp)</Label>
                  <Input type="text" inputMode="numeric"
                    value={form.price ? form.price.toLocaleString("id-ID") : ""}
                    onChange={(e) => setForm({ ...form, price: Number(e.target.value.replace(/\D/g, "")) })} />
                </div>
                <div>
                  <Label>Harga Promo (Rp)</Label>
                  <Input type="text" inputMode="numeric"
                    value={form.promo_price ? form.promo_price.toLocaleString("id-ID") : ""}
                    onChange={(e) => setForm({ ...form, promo_price: Number(e.target.value.replace(/\D/g, "")) })} />
                </div>
              </div>
              <div>
                <Label>Stok</Label>
                <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} />
              </div>
            </TabsContent>

            <TabsContent value="detail" className="space-y-4 mt-4">
              <div>
                <Label>Deskripsi Singkat</Label>
                <Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
              </div>
              <div>
                <Label>Deskripsi Lengkap</Label>
                <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Spesifikasi <span className="text-xs text-muted-foreground">(satu per baris, format <code>kunci: nilai</code>)</span></Label>
                <Textarea rows={4} value={form.specifications} onChange={(e) => setForm({ ...form, specifications: e.target.value })}
                  placeholder={"Bahan: Katun\nUkuran: All size"} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ukuran <span className="text-xs text-muted-foreground">(pisahkan koma)</span></Label>
                  <Input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S, M, L, XL" />
                </div>
                <div>
                  <Label>Warna <span className="text-xs text-muted-foreground">(pisahkan koma)</span></Label>
                  <Input value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Merah, Biru" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="photo" className="space-y-4 mt-4">
              <div>
                <Label>Foto Utama</Label>
                <ImageUpload bucket="store" value={form.main_image} onChange={(v) => setForm({ ...form, main_image: v ?? "" })} />
              </div>
              <div>
                <Label>Galeri Foto</Label>
                {!editing ? (
                  <p className="text-xs text-muted-foreground mt-1">Simpan produk terlebih dahulu untuk mengunggah galeri foto.</p>
                ) : (
                  <>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {gallery.map((g) => (
                        <div key={g.id} className="relative group">
                          <GalleryThumb path={g.image_url} />
                          <button onClick={() => removeGallery(g)} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded p-1 opacity-0 group-hover:opacity-100">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <Input type="file" accept="image/*" multiple className="mt-3"
                      onChange={(e) => uploadGalleryFiles(e.target.files)} disabled={busy} />
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="order" className="space-y-4 mt-4">
              <div>
                <Label>Nomor WhatsApp Admin (override)</Label>
                <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="Kosongkan untuk pakai pengaturan store" />
              </div>
              <div>
                <Label>Cara Pemesanan</Label>
                <Textarea rows={4} value={form.order_instructions} onChange={(e) => setForm({ ...form, order_instructions: e.target.value })} />
              </div>
              <div>
                <Label>Informasi Pembelian</Label>
                <Textarea rows={4} value={form.purchase_info} onChange={(e) => setForm({ ...form, purchase_info: e.target.value })} />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Tutup</Button>
            <Button onClick={save} disabled={busy}>{editing ? "Simpan" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>Galeri foto produk juga akan terhapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDelOpen} onOpenChange={setBulkDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selected.size} produk?</AlertDialogTitle>
            <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus Semua</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LabelSwitch({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <div className="text-sm font-medium">{label}</div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function StatusBadge({ s }: { s: Status }) {
  if (s === "active") return <Badge variant="outline" className="bg-success/15 text-success border-success/30">Aktif</Badge>;
  if (s === "out_of_stock") return <Badge variant="outline" className="bg-orange-500/15 text-orange-700 border-orange-500/30">Habis</Badge>;
  return <Badge variant="outline" className="bg-muted text-muted-foreground">Nonaktif</Badge>;
}

function ProductThumb({ path }: { path: string | null }) {
  const url = useStorageUrl("store", path);
  if (!url) return <div className="w-12 h-12 rounded bg-muted shrink-0" />;
  return <img src={url} alt="" className="w-12 h-12 rounded object-cover border shrink-0" />;
}
function GalleryThumb({ path }: { path: string }) {
  const url = useStorageUrl("store", path);
  if (!url) return <div className="w-full aspect-square rounded bg-muted" />;
  return <img src={url} alt="" className="w-full aspect-square rounded object-cover border" />;
}

function formatIDR(n: number) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}
